import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import {
  PHOTO_ACCEPT_STRING,
  PHOTO_MAX_SIZE_BYTES,
  formatFileSize,
  getPhotoFileRejection,
  isAcceptedPhotoType,
  splitPhotoFiles,
} from './photoFileValidation';
import type { PhotoFileRejection } from './photoFileValidation';
import { convertHeicToJpeg, isHeicFile, isHeicMimeType } from './heicConversion';
import { canDownscale, downscalePhoto, preparePhotoFiles } from './photoDownscale';
import { LiveRegion } from '../LiveRegion/LiveRegion';
import './PhotoUploadDropzone.css';

export interface PhotoUploadDropzoneProps {
  /** Single-file mode: called whenever a valid file is selected. Passes the File object. */
  onChange?: (file: File) => void;
  /**
   * Accept several files per selection/drop (default false: single-file mode with
   * preview and inline error). In multiple mode the dropzone shows no preview and no
   * inline error — every file is validated and reported through `onFilesChange`, so
   * the consumer can render a per-file status list.
   */
  multiple?: boolean;
  /** Multiple mode: called once per selection/drop with the valid and the rejected files. */
  onFilesChange?: (accepted: File[], rejected: PhotoFileRejection[]) => void;
  /** Optional existing preview URL (e.g. current aquarium photo). */
  previewUrl?: string | null;
  /** Override the default 10 MB maximum (in megabytes). */
  maxSizeMB?: number;
  /** Disable the dropzone (e.g. while uploading). */
  disabled?: boolean;
  /** Accessible label for the dropzone region (defaults to a translated "Upload photo"). */
  ariaLabel?: string;
  /** Accessible name of the preview image (defaults to a translated generic description). */
  previewAlt?: string;
  /**
   * Id(s) of external text describing the dropzone — e.g. a consumer's hint or a
   * `FieldError` for a required photo — set as `aria-describedby` on the focusable control.
   */
  ariaDescribedBy?: string;
}

/**
 * Photo picker with drag & drop. The forwarded ref points at the focusable dropzone
 * (role="button"), so a consumer can open the file dialog from another control via
 * `ref.current?.click()`.
 */
export const PhotoUploadDropzone = forwardRef<HTMLDivElement, PhotoUploadDropzoneProps>(function PhotoUploadDropzone({
  onChange,
  multiple = false,
  onFilesChange,
  previewUrl = null,
  maxSizeMB,
  disabled = false,
  ariaLabel,
  previewAlt,
  ariaDescribedBy,
}, ref) {
  const { t } = useTranslation();
  // In multiple mode the accessible name matches the visible instruction (2.5.3 label in name).
  const resolvedAriaLabel =
    ariaLabel ??
    (multiple ? t('common.photoDropzone.labelMultiple') : t('common.photoDropzone.defaultAriaLabel'));
  const resolvedPreviewAlt = previewAlt ?? t('common.photoDropzone.previewAlt');
  const maxBytes = maxSizeMB !== undefined ? maxSizeMB * 1024 * 1024 : PHOTO_MAX_SIZE_BYTES;
  const [preview, setPreview] = useState<string | null>(previewUrl ?? null);
  const [error, setError] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isDragReject, setIsDragReject] = useState(false);
  // Set while photos are prepared in the browser: HEIC → JPEG (heicConversion.ts)
  // or a photo over the limit is downscaled (photoDownscale.ts).
  const [preparing, setPreparing] = useState<'converting' | 'resizing' | null>(null);
  const busy = disabled || preparing !== null;
  const prevObjectUrl = useRef<string | null>(null);
  const dragCounterRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPreview(previewUrl ?? null);
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      if (prevObjectUrl.current !== null) {
        URL.revokeObjectURL(prevObjectUrl.current);
      }
    };
  }, []);

  const processFile = useCallback(
    async (selected: File) => {
      if (busy) return;

      let file = selected;
      const heic = isHeicFile(selected);
      if (heic || canDownscale(selected, maxBytes)) {
        setError(null);
        setPreparing(heic ? 'converting' : 'resizing');
        try {
          if (heic) {
            try {
              file = await convertHeicToJpeg(selected);
            } catch {
              setError(t('common.photoDropzone.heicFailed'));
              return;
            }
          }
          file = await downscalePhoto(file, maxBytes);
        } finally {
          setPreparing(null);
        }
      }

      const rejection = getPhotoFileRejection(file, maxBytes);
      if (rejection === 'type') {
        setError(
          t('common.photoDropzone.invalidType', {
            type: file.type || t('common.photoDropzone.unknownType'),
          }),
        );
        return;
      }

      if (rejection === 'size') {
        const sizeMB = maxBytes / (1024 * 1024);
        setError(
          t('common.photoDropzone.tooLarge', { maxMb: sizeMB, size: formatFileSize(file.size) }),
        );
        return;
      }

      setError(null);

      if (prevObjectUrl.current !== null) {
        URL.revokeObjectURL(prevObjectUrl.current);
        prevObjectUrl.current = null;
      }

      const objectUrl = URL.createObjectURL(file);
      prevObjectUrl.current = objectUrl;
      setPreview(objectUrl);
      onChange?.(file);
    },
    [busy, maxBytes, onChange, t],
  );

  const processFiles = useCallback(
    async (selected: File[]) => {
      if (busy || selected.length === 0) return;
      let files = selected;
      const heic = selected.some(isHeicFile);
      if (heic || selected.some((file) => canDownscale(file, maxBytes))) {
        setPreparing(heic ? 'converting' : 'resizing');
        try {
          files = await preparePhotoFiles(selected, maxBytes);
        } finally {
          setPreparing(null);
        }
      }
      const { accepted, rejected } = splitPhotoFiles(files, maxBytes);
      onFilesChange?.(accepted, rejected);
    },
    [busy, maxBytes, onFilesChange],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (multiple) {
        const files = Array.from(e.target.files ?? []);
        // Reset so selecting the same files again fires another change event.
        e.target.value = '';
        void processFiles(files);
        return;
      }
      const file = e.target.files?.[0];
      if (file !== undefined) {
        void processFile(file);
      }
    },
    [multiple, processFile, processFiles],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (busy) return;
      e.preventDefault();
      dragCounterRef.current++;
      setIsDragActive(true);
      const fileItems = Array.from(e.dataTransfer.items).filter((item) => item.kind === 'file');
      const invalid = fileItems.filter((item) => !isAcceptedPhotoType(item.type) && !isHeicMimeType(item.type));
      // Multiple mode still uploads the valid files of a mixed drop, so it only
      // signals rejection when nothing in the drag is acceptable.
      setIsDragReject(
        multiple ? fileItems.length > 0 && invalid.length === fileItems.length : invalid.length > 0,
      );
    },
    [busy, multiple],
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragActive(false);
      setIsDragReject(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragActive(false);
      setIsDragReject(false);
      if (busy) return;
      if (multiple) {
        void processFiles(Array.from(e.dataTransfer.files));
        return;
      }
      const file = e.dataTransfer.files[0];
      if (file !== undefined) {
        void processFile(file);
      }
    },
    [busy, multiple, processFile, processFiles],
  );

  const openFilePicker = useCallback(() => {
    if (!busy) {
      inputRef.current?.click();
    }
  }, [busy]);

  let statusClass = 'photo-upload-dropzone';
  if (busy) statusClass += ' photo-upload-dropzone--disabled';
  else if (isDragReject) statusClass += ' photo-upload-dropzone--drag-reject';
  else if (isDragActive) statusClass += ' photo-upload-dropzone--drag-active';
  else if (!multiple && preview !== null) statusClass += ' photo-upload-dropzone--has-preview';

  return (
    <div>
      {/* The file input sits outside the role="button" dropzone so no focusable
          control is nested inside another interactive element (axe nested-interactive). */}
      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_ACCEPT_STRING}
        multiple={multiple}
        disabled={busy}
        onChange={handleInputChange}
        aria-hidden="true"
        data-testid="photo-dropzone-input"
        className="photo-upload-dropzone__input"
        tabIndex={-1}
      />
      <div
        ref={ref}
        className={statusClass}
        aria-label={resolvedAriaLabel}
        aria-describedby={ariaDescribedBy}
        aria-disabled={busy}
        aria-busy={preparing !== null || undefined}
        tabIndex={busy ? -1 : 0}
        role="button"
        data-testid="photo-dropzone"
        onClick={openFilePicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            // Keep Space from scrolling the page while it activates the control.
            e.preventDefault();
            openFilePicker();
          }
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {!multiple && preview !== null ? (
          <div className="photo-upload-dropzone__preview">
            <img
              src={preview}
              alt={resolvedPreviewAlt}
              className="photo-upload-dropzone__preview-img"
            />
            <div className="photo-upload-dropzone__preview-overlay" aria-hidden="true">
              <span className="photo-upload-dropzone__preview-change-label">
                {t('common.photoDropzone.replaceLabel')}
              </span>
            </div>
          </div>
        ) : (
          <div className="photo-upload-dropzone__content" aria-hidden="true">
            <svg
              className="photo-upload-dropzone__icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
              focusable="false"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 18h16.5M8.25 3h7.5A2.25 2.25 0 0118 5.25v13.5A2.25 2.25 0 0115.75 21H8.25A2.25 2.25 0 016 18.75V5.25A2.25 2.25 0 018.25 3z"
              />
            </svg>
            {isDragActive ? (
              <p className="photo-upload-dropzone__drag-label">
                {multiple ? t('common.photoDropzone.dropMultiple') : t('common.photoDropzone.drop')}
              </p>
            ) : (
              <>
                <p className="photo-upload-dropzone__label">
                  {multiple ? t('common.photoDropzone.labelMultiple') : t('common.photoDropzone.label')}
                </p>
                <p className="photo-upload-dropzone__hint">
                  {multiple
                    ? t('common.photoDropzone.hintMultiple', { maxMb: maxBytes / (1024 * 1024) })
                    : t('common.photoDropzone.hint', { maxMb: maxBytes / (1024 * 1024) })}
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {preparing !== null && (
        <LiveRegion className="photo-upload-dropzone__converting" data-testid="photo-dropzone-converting">
          {t(preparing === 'converting' ? 'common.photoDropzone.converting' : 'common.photoDropzone.resizing')}
        </LiveRegion>
      )}
      <div aria-live="assertive" aria-atomic="true" data-testid="photo-dropzone-status">
        {error !== null && (
          <p
            role="alert"
            className="photo-upload-dropzone__error"
            data-testid="photo-dropzone-error"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );
});

export type { PhotoFileRejection } from './photoFileValidation';
